import { Image } from 'marketing-site/src/library/elements/Image'
import { Text } from 'marketing-site/src/library/elements/Text'
import carouselArrow from 'marketing-site/src/library/images/carousel-arrow.svg'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import React, { useEffect, useState } from 'react'
import { ITimeline as IProps } from './index'
import styles from './style.scss'

export function InnovationTimeline({ heading, subheading, cards, image, isMobile }: IProps) {
  const [timeline, setTimeline] = useState({
    startDate: new Date(),
    endDate: new Date(),
    lengthInDays: 0,
  })
  const [activeCard, setActiveCard] = useState(6)
  const { translate } = useTranslate()

  function formatDate(date: string) {
    const timestamp = new Date(date)
    return `${timestamp.getMonth() + 1}/${timestamp.getDate()}/${timestamp.getFullYear()}`
  }

  function getCardPosition(date: string) {
    const daysSinceTimelineStart =
      (new Date(date).getTime() - timeline.startDate.getTime()) / (1000 * 60 * 60 * 24)

    return `${(daysSinceTimelineStart / timeline.lengthInDays) * 100}%`
  }

  function renderOldestDate() {
    const options: Intl.DateTimeFormatOptions = { month: 'long' }
    const month = new Intl.DateTimeFormat('en-US', options).format(timeline.startDate).slice(0, 3)
    const year = timeline.startDate.getFullYear()
    return `${month} ${year}`
  }

  // get the oldest & newest post dates from the passed cards and update state
  function getTimelineData() {
    const dates = {
      oldest: new Date(cards[0].date),
      newest: new Date(cards[cards.length - 1].date),
    }
    cards.forEach((card) => {
      const timestamp = new Date(card.date)
      if (timestamp < dates.oldest) {
        dates.oldest = timestamp
      } else if (timestamp > dates.newest) {
        dates.newest = timestamp
      }
    })

    setTimeline({
      ...timeline,
      startDate: dates.oldest,
      endDate: dates.newest,
      lengthInDays: (dates.newest.getTime() - dates.oldest.getTime()) / (1000 * 60 * 60 * 24),
    })
  }

  function prevCard() {
    let prevCard = activeCard - 1
    if (prevCard < 0) prevCard = cards.length - 1
    setActiveCard(prevCard)
  }

  function nextCard() {
    let nextCard = activeCard + 1
    if (nextCard === cards.length) nextCard = 0
    setActiveCard(nextCard)
  }

  useEffect(() => {
    getTimelineData()

    // defaults focus to middle timeline card on tablet & larger
    const timelineEl = document.querySelector('.timeline')
    timelineEl?.addEventListener('mouseover', function () {
      timelineEl.querySelector('.active')?.classList.remove('active')
    })
    timelineEl?.addEventListener('mouseout', function () {
      timelineEl.querySelectorAll('li')[activeCard].classList.add('active')
    })
  }, [])

  return (
    <div className="timeline-section-wrapper">
      <div className="timeline-section">
        <div className="timeline__image">{image && <Image {...image} />}</div>
        <h1>
          <Text brand="default" size="heading-2">
            {heading}
          </Text>
        </h1>
        <p>
          <Text brand="default" size="heading-5">
            {subheading}
          </Text>
        </p>

        <div className="timeline" aria-hidden={true}>
          <div>
            {isMobile ? (
              <div>
                <button
                  className="timeline__arrow-btn timeline__arrow-btn--prev"
                  onClick={prevCard}
                >
                  <img src={carouselArrow} alt="Previous item" />
                </button>
                <button
                  className="timeline__arrow-btn timeline__arrow-btn--next"
                  onClick={nextCard}
                >
                  <img src={carouselArrow} alt="Next item" />
                </button>

                <div className="timeline__card">
                  <p className="timeline__card-date">{formatDate(cards[activeCard].date)}</p>
                  <p className="timeline__card-title">{cards[activeCard].title}</p>
                  <a href={cards[activeCard].cta.url} className="timeline__card-link">
                    {cards[activeCard].cta.text}
                  </a>
                </div>

                <ul className="timeline__list">
                  {cards.map((card, index) => {
                    return (
                      <li
                        key={index}
                        style={{ left: getCardPosition(card.date) }}
                        className={index === activeCard ? 'mobile-active' : ''}
                      >
                        <div
                          className={`timeline__card-icon timeline__card-icon--${card.type.toLowerCase()}`}
                        ></div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : (
              <ul className="timeline__list">
                {cards.map((card, index) => {
                  return (
                    <li
                      key={index}
                      style={{ left: getCardPosition(card.date) }}
                      className={index === 6 ? 'active' : ''}
                    >
                      <div className="timeline__card">
                        <p className="timeline__card-date">{formatDate(card.date)}</p>
                        <p className="timeline__card-title">{card.title}</p>
                        <a href={card.cta.url} className="timeline__card-link">
                          {card.cta.text}
                        </a>
                      </div>
                      <div
                        className={`timeline__card-icon timeline__card-icon--${card.type.toLowerCase()}`}
                      ></div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="timeline__bar"></div>
          <div className="timeline__labels">
            <span>{renderOldestDate()}</span>
            <span>{translate('Today')}</span>
          </div>
          <div className="timeline__legend">
            <span>
              <Text brand="default" size="body">
                {translate('All solutions')}
              </Text>
            </span>
            <span>
              <Text brand="default" size="body">
                {translate('Convert')}
              </Text>
            </span>
            <span>
              <Text brand="default" size="body">
                {translate('Engage')}
              </Text>
            </span>
            <span>
              <Text brand="default" size="body">
                {translate('Support')}
              </Text>
            </span>
          </div>
        </div>
      </div>

      <style jsx>{styles}</style>
    </div>
  )
}
